import React, { useCallback, useEffect, useRef } from "react";
import { Spinner, TableHeader } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import RoleInfoSection from "./RoleInfoSection/RoleInfoSection";
import PermissionModuleSection from "./PermissionModuleSection/PermissionModuleSection";
import useRolePermissions from "./hooks/useRolePermissions";
import { Col } from "react-bootstrap";
import RolePermissionsContext from "./context/RolePermissionsContext";
import RoleDetailsButtons from "./RoleDetailsButtons/RoleDetailsButtons";
import ToastComponent from "../../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../../hooks/useToast";
import { RoleDetailsPageService } from "./service/RoleDetailsPageService";
import RoleManagementInfoBanner from "../RoleManagementInfoBanner";

export type Mode = "create" | "edit" | "view" | "clone" | "restore";
export type RoleType = "name" | "permissions";

type RoleDetailsPageProps = {
    mode: Mode;
};

const RoleDetailsPage: React.FC<RoleDetailsPageProps> = ({ mode }) => {
    const { t: translate } = useTranslation();
    const { branchId, roleId } = useParams();

    const [roleNameError, setRoleNameError] = React.useState<string | null>(null);

    const { showToast, setShowToast, toggleShowToast, toastText, bgColor, setBgColor } = useToast();
    const pageTitle = RoleDetailsPageService.getPageTitle(mode);
    const errorBGColor = "#DC3545";

    const roleInfoRef = useRef<HTMLDivElement | null>(null);

    const clearRoleNameError = useCallback(() => {
        setRoleNameError(null);
    }, []);

    const onSubmitRole = useCallback(
        (roleType: RoleType, error: string | null) => {
            if (roleType === "name") {
                setRoleNameError(error);
            }

            if (roleType === "permissions") {
                setBgColor(errorBGColor);
                toggleShowToast(translate(error));
            }
        },
        [toggleShowToast, translate, setBgColor, errorBGColor]
    );

    useEffect(() => {
        if (roleNameError) {
            roleInfoRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [roleNameError]);

    const {
        permissions,
        isLoading,
        onChangeRoleName,
        onChangeRoleDescription,
        onChangePermissionScope,
        onChangePermissionAction,
        onChangePermissionCategory,
        onChangeAllPermissions,
    } = useRolePermissions(branchId ? Number(branchId) : null, roleId ? Number(roleId) : null, mode);

    return (
        <>
            <RoleManagementInfoBanner variant="details" />

            <TableHeader
                title={translate(pageTitle)}
                paths={[
                    {
                        path: `/branch/access-settings/${branchId}`,
                        label: translate("Access settings"),
                    },
                    {
                        path: `/branch/role-management/${branchId}`,
                        label: translate("Role Management"),
                    },
                    {
                        path: ``,
                        label: translate(pageTitle),
                    },
                ]}
            />

            {isLoading && <Spinner />}

            {!isLoading && permissions && (
                <RolePermissionsContext.Provider
                    value={{
                        permissions,
                        onChangeRoleName,
                        onChangeRoleDescription,
                        onChangePermissionScope,
                        onChangePermissionAction,
                        onChangePermissionCategory,
                        onChangeAllPermissions,
                        mode,
                    }}
                >
                    <Col lg={12} xl={9} className="mb-5">
                        <div ref={roleInfoRef} style={{ scrollMarginTop: 80 }}>
                            <RoleInfoSection roleNameError={roleNameError} clearRoleNameError={clearRoleNameError} />
                        </div>

                        <PermissionModuleSection />

                        <RoleDetailsButtons
                            onSubmitRole={onSubmitRole}
                            roleNameError={roleNameError}
                            roleId={roleId ? Number(roleId) : null}
                        />
                    </Col>
                </RolePermissionsContext.Provider>
            )}

            {showToast && (
                <ToastComponent show={showToast} setShow={setShowToast} bgColor={bgColor} text={translate(toastText)} />
            )}
        </>
    );
};

export default RoleDetailsPage;
